import type { SearchSliceNode, StreamSliceNode } from '@/analysis/searchContract'
import type { Selection } from '@/views/deepDiveView/types'

/**
 * Decide what to deep-dive into.
 *
 *  1. If the user explicitly drilled into a stream, use that.
 *  2. If the search returned exactly one node, use it (auto-dive).
 *  3. Otherwise dive into the whole dimension via the wildcard '*' value
 *     so the chart still renders aggregate timeseries.
 *
 * Returns null only when the dimension itself is missing.
 */
export const resolveSelection = (
  drillStream: string | null | undefined,
  dimension: string | null | undefined,
  searchNodes: ReadonlyArray<SearchSliceNode> | null | undefined,
): Selection | null => {
  if (drillStream) {
    return { dimension: 'stream', value: drillStream, sliceType: 'global', sliceValue: '*' }
  }
  if (!dimension) return null

  if (Array.isArray(searchNodes) && searchNodes.length === 1) {
    const n = searchNodes[0]!
    const v = n.kind === 'stream'
      ? String((n as StreamSliceNode).streamId || n.value || '').trim()
      : String(n.value ?? n.label ?? '').trim()
    if (v) {
      if (n.kind === 'stream') {
        const sn = n as StreamSliceNode
        return {
          dimension: 'stream',
          value: v,
          sliceType: sn.parentSliceType ?? 'global',
          sliceValue: sn.parentSliceValue ?? '*',
        }
      }
      return { dimension, value: v }
    }
  }
  return { dimension, value: '*' }
}

/**
 * Find the search node that corresponds to the current selection — used to
 * append a single live tick to the chart between server fetches.
 */
export const findMatchingNode = (
  nodes: ReadonlyArray<SearchSliceNode> | null | undefined,
  value: string,
  sliceType?: string,
  sliceValue?: string,
): SearchSliceNode | null => {
  if (!Array.isArray(nodes)) return null
  for (const n of nodes) {
    if (n.kind === 'stream') {
      const sn = n as StreamSliceNode
      const id = String(sn.streamId || sn.value || '')
      if (id !== value) continue
      if (sliceType != null && sliceValue != null) {
        const pt = sn.parentSliceType ?? 'global'
        const pv = sn.parentSliceValue ?? '*'
        if (pt !== sliceType || pv !== sliceValue) continue
      }
      return n
    }
    const id = String(n.value ?? '')
    const label = String(n.label ?? '')
    if (id === value || label === value) return n
  }
  return null
}
